import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Mail, CheckCircle2, Search, AlertTriangle, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import TourManager from "@/components/admin/TourManager";
import { api } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mb-1">{label}</div>
    <div className="text-xl font-bold text-ink-primary">{value}</div>
  </div>
);

const STATUS_CLS = {
  complete: "text-emerald-300 bg-emerald-500/15",
  in_progress: "text-amber-300 bg-amber-500/15",
  skipped: "text-ink-muted bg-bg-elevated",
  not_started: "text-ink-muted bg-bg-elevated",
};

export default function AdminOnboarding() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = async (query = "") => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/admin/onboarding/overview"),
        api.get(`/admin/onboarding/users?q=${encodeURIComponent(query)}`),
        api.get("/admin/onboarding/errors?limit=30"),
      ]);
      setOverview(a.data); setUsers(b.data.users || []); setErrors(c.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed (admin only)"); }
  };
  useEffect(() => { load(); }, []);

  const act = async (uid, action, body) => {
    setBusy(`${uid}:${action}`);
    try {
      const { data } = await api.post(`/admin/onboarding/user/${uid}/${action}`, body || {});
      toast.success(action === "remind" ? `Reminder sent to ${data.sent_to}` : "Done");
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="admin-onboarding-page">
      <PageHeader eyebrow="// ADMIN · ONBOARDING" title="Onboarding"
        description="Track setup completion, unstick users, and send reminders." />
      <TourManager />
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Total users" value={overview.total_users} testid="onbadm-total" />
          <Stat label="Wizards started" value={overview.wizards_started} testid="onbadm-started" />
          <Stat label="Completed" value={overview.by_status?.complete || 0} testid="onbadm-complete" />
          <Stat label="Skipped" value={overview.by_status?.skipped || 0} testid="onbadm-skipped" />
          <Stat label="Avg completion" value={`${overview.avg_completion_pct}%`} testid="onbadm-avg" />
        </div>
      )}
      {overview && Object.keys(overview.stopped_at_step || {}).length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Users stopped at step</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(overview.stopped_at_step).sort((a, b) => Number(a[0]) - Number(b[0])).map(([s, n]) => (
              <span key={s} className="rounded-md border border-line bg-bg-elevated px-2.5 py-1 text-xs text-ink-secondary">
                Step {s}: <span className="text-ink-primary font-semibold">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="rounded-xl border border-line bg-bg-surface overflow-hidden">
        <div className="p-4 flex items-center gap-2 border-b border-line">
          <Search className="h-4 w-4 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)} placeholder="Search email or name… (Enter)"
            data-testid="onbadm-search"
            className="flex-1 bg-transparent text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left font-mono text-[9px] uppercase tracking-wider text-ink-muted border-b border-line">
                <th className="px-4 py-2.5">User</th><th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Step</th><th className="px-3 py-2.5">Progress</th>
                <th className="px-3 py-2.5">Connections</th><th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`onbadm-row-${u.user_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="text-ink-primary">{u.name || "—"}</div>
                    <div className="text-[10px] text-ink-muted">{u.email}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_CLS[u.onboarding_status] || STATUS_CLS.not_started}`}>
                      {u.onboarding_status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-ink-secondary">{u.current_step || "—"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1 rounded-full bg-bg-elevated overflow-hidden">
                        <div className="h-full bg-brand-amber" style={{ width: `${u.progress_pct}%` }} />
                      </div>
                      <span className="text-ink-secondary">{u.progress_pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-ink-secondary">
                    {u.social_connected} social{u.broadcast_connected ? ` · ${u.broadcast_provider || "stream"} ✓` : ""}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button title="Send reminder" onClick={() => act(u.user_id, "remind")}
                        disabled={busy === `${u.user_id}:remind`} data-testid={`onbadm-remind-${u.user_id}`}
                        className="rounded-md border border-line bg-bg-elevated p-1.5 text-ink-secondary hover:text-brand-amber transition disabled:opacity-50">
                        {busy === `${u.user_id}:remind` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                      </button>
                      <button title="Mark current step complete"
                        onClick={() => act(u.user_id, "complete-step", { step: u.current_step || 1 })}
                        disabled={busy === `${u.user_id}:complete-step` || !u.current_step}
                        data-testid={`onbadm-complete-${u.user_id}`}
                        className="rounded-md border border-line bg-bg-elevated p-1.5 text-ink-secondary hover:text-emerald-300 transition disabled:opacity-50">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </button>
                      <button title="Reset onboarding" onClick={() => act(u.user_id, "reset")}
                        disabled={busy === `${u.user_id}:reset`} data-testid={`onbadm-reset-${u.user_id}`}
                        className="rounded-md border border-line bg-bg-elevated p-1.5 text-ink-secondary hover:text-rose-300 transition disabled:opacity-50">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {errors.length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-300" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-amber-300">Recent onboarding errors</span>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="onbadm-errors">
            {errors.map((e) => (
              <div key={e.error_id} className="rounded-md border border-line bg-bg-elevated/50 px-3 py-2 text-[11px]">
                <span className="font-mono text-ink-muted">{e.where}</span>
                <span className="text-ink-secondary ml-2">{e.message}</span>
                <span className="text-ink-muted ml-2">{new Date(e.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
